import { authnClient } from '@frontend/auth';
import { Tracer } from '@frontend/tracer';
import { sentry } from '@frontend/tracking';

export const tracer = new Tracer();
export const tracerHeader = 'X-Weave-Trace-ID';

/**
 * Configures the retry mechanism with tracing for server errors
 * @param request The request that failed
 * @param response The failed response
 * @param attempts The number of retry attempts so far
 * @returns Modified request with trace header or null to stop retrying
 */
export const retryWithTrace = (request: Request, response: Response, attempts: number): Request | null => {
  if (response.ok) {
    return null;
  }

  if (response.status < 500 && response.status !== 401) {
    return null;
  }

  if (attempts > 2) {
    return null;
  }

  const id = tracer.generateId();
  const link = tracer.generateLink();
  console.log(
    `%c${link}`,
    `font-size:12px;
     padding:20px;
     border: 1px solid #8e959e;
     border-radius: 0px 5px 5px 5px;
     margin-bottom: 10px;`
  );

  request.headers.set(tracerHeader, id);
  return request;
};

/**
 * Handles 401 error responses by triggering a sign-out
 * @param triggerInactiveSignOut Function to trigger sign out process
 * @returns Response handler function
 */
export const handle401Error = (triggerInactiveSignOut: () => void, weaveTokenBufferActive: () => boolean) => {
  return async (response: Response): Promise<Response> => {
    if (response.status === 401) {
      if (authnClient.isUserAuthenticated() || weaveTokenBufferActive()) {
        sentry.error({
          topic: 'auth',
          error: `401 received from ${response.url} but Weave token still valid`,
          addContext: {
            name: `is user authenticated = ${authnClient.isUserAuthenticated()}, weave token buffer active = ${weaveTokenBufferActive}`,
            context: {},
          },
        });
      }
      triggerInactiveSignOut();
      return Promise.resolve(response);
    }
    return Promise.resolve(response);
  };
};

/**
 * Randomly fails requests to test error handling
 * @param req The request to potentially fail
 * @returns The request or throws an error
 */
export const chaosMonkeyMiddleware = async (req: Request): Promise<Request> => {
  // Give a 50% chance of blocking a network request
  if (Math.random() > 0.5) {
    console.error('Chaos Monkey Throwing Error', req.url);
    throw new Error('Chaos Monkey');
  }
  return req;
};

/**
 * Chaos monkey middleware with memory of previously blocked URLs
 * @param req The request to potentially fail
 * @param chaosBlockedURLS Array of already blocked URLs
 * @param chaosAllowedURLS Array of allowed URLs
 * @param setChaosBlockedURLS Function to update blocked URLs
 * @param setChaosAllowedURLS Function to update allowed URLs
 * @returns The request or throws an error
 */
export const chaosMonkeyWithMemoryMiddleware = (
  chaosBlockedURLS: string[],
  chaosAllowedURLS: string[],
  setChaosBlockedURLS: (updater: (prev: string[]) => string[]) => void,
  setChaosAllowedURLS: (updater: (prev: string[]) => string[]) => void
) => {
  return async (req: Request): Promise<Request> => {
    // Give a 50% chance of blocking a url for the duration of the session
    // it maintains the blocked and allowed urls in state so we don't grind the network requests to a halt
    if (chaosBlockedURLS.includes(req.url) || (!chaosAllowedURLS.includes(req.url) && Math.random() > 0.5)) {
      if (!chaosBlockedURLS.includes(req.url)) {
        setChaosBlockedURLS((prev: string[]) => [...prev, req.url]);
      }
      console.error('Chaos Monkey Throwing Error', req.url);
      throw new Error('Chaos Monkey');
    } else {
      setChaosAllowedURLS((prev: string[]) => [...prev, req.url]);
    }
    return req;
  };
};
